import * as THREE from 'three';
import { createRandom } from '../core/random';
import type { QualityTier } from './RenderEngine';

export class EnvironmentSystem {
  public readonly group = new THREE.Group();
  private readonly stars: THREE.InstancedMesh;
  private readonly motes: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial>;
  private readonly corridor = new THREE.Group();
  private travel = 0;

  public constructor(quality: QualityTier) {
    this.group.name = 'procedural-depth-field';
    const count = quality === 'low' ? 420 : quality === 'medium' ? 760 : 1180;
    const geometry = new THREE.IcosahedronGeometry(.027, 0);
    const material = new THREE.MeshBasicMaterial({ color: 0xd5e0dc, transparent: true, opacity: .72, depthWrite: false });
    this.stars = new THREE.InstancedMesh(geometry, material, count); this.stars.frustumCulled = false;
    const random = createRandom('astra-depth-field'); const matrix = new THREE.Matrix4(); const scale = new THREE.Vector3(); const rotation = new THREE.Quaternion();
    for (let index = 0; index < count; index += 1) {
      const z = random.range(-390, 20); const angle = random.range(0, Math.PI * 2); const radius = random.range(8, 52);
      const position = new THREE.Vector3(Math.cos(angle) * radius, Math.sin(angle) * radius * .62, z);
      const size = random.range(.45, 2.4); scale.setScalar(size); matrix.compose(position, rotation, scale); this.stars.setMatrixAt(index, matrix);
    }
    this.stars.instanceMatrix.needsUpdate = true; this.group.add(this.stars);
    this.motes = this.buildMotes(quality === 'low' ? 80 : quality === 'medium' ? 150 : 240); this.group.add(this.motes);
    this.buildCorridor(); this.group.add(this.corridor);
  }

  public update(delta: number, elapsed: number, speed: number, active: boolean): void {
    if (active) this.travel += delta * speed;
    this.stars.position.z = this.travel % 205;
    this.motes.position.z = (this.travel * 1.26) % 92;
    this.motes.rotation.z = Math.sin(elapsed * .055) * .08;
    this.corridor.position.z = this.travel % 64;
    this.corridor.rotation.z = Math.sin(elapsed * .08) * .035;
  }

  public setPalette(accent: number, secondary: number): void {
    (this.motes.material as THREE.PointsMaterial).color.setHex(accent);
    for (const child of this.corridor.children) if (child instanceof THREE.Line) (child.material as THREE.LineBasicMaterial).color.setHex(secondary);
  }

  public dispose(): void { this.group.removeFromParent(); }

  private buildMotes(count: number): THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> {
    const random = createRandom('astra-drifting-motes'); const values = new Float32Array(count * 3);
    for (let index = 0; index < count; index += 1) { const angle = random.range(0, Math.PI * 2); const radius = random.range(6.5, 22); values[index * 3] = Math.cos(angle) * radius; values[index * 3 + 1] = Math.sin(angle) * radius * .58; values[index * 3 + 2] = random.range(-98, -4); }
    const geometry = new THREE.BufferGeometry(); geometry.setAttribute('position', new THREE.BufferAttribute(values, 3));
    return new THREE.Points(geometry, new THREE.PointsMaterial({ color: 0xca8b67, size: .085, transparent: true, opacity: .34, blending: THREE.AdditiveBlending, depthWrite: false }));
  }

  private buildCorridor(): void {
    const material = new THREE.LineBasicMaterial({ color: 0x68858a, transparent: true, opacity: .12, depthWrite: false });
    for (let strand = 0; strand < 6; strand += 1) {
      const points: THREE.Vector3[] = []; const phase = strand / 6 * Math.PI * 2;
      for (let segment = 0; segment <= 96; segment += 1) { const z = -segment; const angle = segment * .08 + phase; points.push(new THREE.Vector3(Math.cos(angle) * 7.2, Math.sin(angle) * 4.25, z)); }
      const geometry = new THREE.BufferGeometry().setFromPoints(points); this.corridor.add(new THREE.Line(geometry, material));
    }
    for (let ring = 0; ring < 6; ring += 1) {
      const points: THREE.Vector3[] = [];
      for (let segment = 0; segment <= 64; segment += 1) { const angle = segment / 64 * Math.PI * 2; points.push(new THREE.Vector3(Math.cos(angle) * 7.2, Math.sin(angle) * 4.25, -ring * 16)); }
      this.corridor.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), material));
    }
  }
}
